import React, { FC, useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import debounce from 'lodash/debounce';
import { BankingService } from 'services';

export type BrazilianBank = {
  ispb: string;
  name: string;
  code?: string;
};

type Props = {
  value: string; // ISPB or bank code
  onChange: (ispb: string, code?: string) => void;
  error?: boolean;
  helperText?: string | false;
};

const bankingService = new BankingService();

const BrazilianBankSearch: FC<Props> = ({ value, onChange, error, helperText }) => {
  const [options, setOptions] = useState<BrazilianBank[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [locked, setLocked] = useState(false);

  const selectedBank = useMemo(() => {
    return options.find((b) => b.ispb === value || b.code === value);
  }, [options, value]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (query.length < 3) {
          setOptions([]);
          return;
        }

        setLoading(true);
        try {
          const result = await bankingService.getInstitutionsList(query);
          setOptions(result);
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 400),
    []
  );

  useEffect(() => {
    if (!locked) {
      debouncedSearch(inputValue);
    }
  }, [inputValue, locked, debouncedSearch]);

  useEffect(() => {
    const preset = (value || '').trim();
    if (!preset || locked) return;

    const loadInitial = async () => {
      setLoading(true);
      try {
        const list = await bankingService.getInstitutionsList();
        setOptions(list as BrazilianBank[]);
        const found = list.find((b) => b.ispb === preset || (b.code && b.code === preset));
        if (found) {
          setInputValue(found.name);
          setLocked(true);
          onChange(found.ispb, found.code);
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };

    loadInitial();
  }, [value, locked, onChange]);

  const handleClear = () => {
    onChange('');
    setInputValue('');
    setOptions([]);
    setLocked(false);
  };

  return (
    <Autocomplete
      fullWidth
      disableClearable
      options={options}
      loading={loading}
      value={selectedBank ?? undefined}
      inputValue={inputValue}
      noOptionsText="Nenhum banco encontrado"
      isOptionEqualToValue={(o, v) => o.ispb === v.ispb}
      getOptionLabel={(o) => `${o.name} (${o.code || o.ispb})`}
      onInputChange={(_, newValue, reason) => {
        if (locked) return;
        if (reason === 'input') {
          setInputValue(newValue);
        }
      }}
      onChange={(_, newValue) => {
        if (newValue) {
          onChange(newValue.ispb, newValue.code);
          setInputValue(newValue.name);
          setLocked(true);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label="Instituição financeira *"
          placeholder="Digite ao menos 3 caracteres"
          error={error}
          helperText={helperText}
          sx={{ mb: 3 }}
          InputLabelProps={{
            shrink: true,
            sx: { fontSize: '16px', fontWeight: 500 },
          }}
          InputProps={{
            ...params.InputProps,
            readOnly: locked,
            endAdornment: (
              <>
                {locked && (
                  <IconButton size="small" onClick={handleClear} aria-label="Limpar banco selecionado">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                {loading && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: { fontSize: '14px' },
          }}
        />
      )}
    />
  );
};

export default BrazilianBankSearch;
